// Cross-filtering, which is the whole of what makes a dashboard interactive.
//
// `#59` names it the least standard behaviour in the application and the most
// underestimated item in the phase, so it is a module of its own with tests
// rather than three conditions inside a component.
//
// THE PANES SHARE ONE DIMENSION, THE PRODUCTION ORDER. A barrier carries
// `order_id`, a two-week row carries `order_id`, a daily-production row carries
// `order_id`, and an order is one. Nothing else is common to all of them --
// `Plant` is on a barrier and not on a two-week row, `Week` is on a two-week
// row and not on a barrier -- which is why a selection is an order id rather
// than a general filter over columns.

export type PaneId = 'orders' | 'two-week' | 'barriers' | 'daily';

export interface Selection {
  orderId: number;
  /** Which pane the person clicked. */
  from: PaneId;
  /** The clicked row's own grid id, which is NOT the order id.
   *
   *  THIS FIELD EXISTS BECAUSE ITS ABSENCE WAS A BUG. The highlight was matched
   *  by comparing a pane's row id against `orderId`, and a pane's row id is its
   *  own key -- `two_week_production_id`, `barrier_number`. So the highlight
   *  landed on whichever unrelated row happened to have that number as its key,
   *  and on the barriers pane, where the numbers do not overlap, on nothing at
   *  all. Only the `Orders` pane was ever right, because there the row id and
   *  the order id are the same column. Found in the phase 6 sign-off. */
  rowId: string;
  /** What to call it in the "clear filter" control: the job number where there
   *  is one, the id where there is not. */
  label: string;
}

/** The order id a pane should narrow to, or null for "show everything".
 *
 *  The pane the selection was made in is never narrowed by it. Filtering the
 *  grid you are choosing from down to the single row you chose removes the list
 *  you were choosing from, and with it any way to choose differently. */
export function filteredBy(selection: Selection | null, pane: PaneId): number | null {
  if (!selection || selection.from === pane) return null;
  return selection.orderId;
}

/** The row to draw as selected in a pane: only in the pane the selection was
 *  made in, so one click never highlights rows in three places.
 *
 *  It returns the clicked row's own id rather than the order id, which is the
 *  whole of the fix described on `Selection.rowId`. */
export function selectedIdIn(selection: Selection | null, pane: PaneId): string | null {
  return selection && selection.from === pane ? selection.rowId : null;
}

/** Applying a selection to a pane's rows.
 *
 *  A row whose `order_id` is null never survives a filter, which is correct and
 *  worth stating: `order_id` is nullable on `barrier`, so a barrier belonging to
 *  no order is not part of any order's story. */
export function applySelection<T extends { order_id?: unknown }>(
  rows: T[],
  selection: Selection | null,
  pane: PaneId
): T[] {
  const orderId = filteredBy(selection, pane);
  return orderId === null ? rows : rows.filter((row) => row.order_id === orderId);
}

/** What a click on a row means.
 *
 *  Clicking the selected row again clears the filter, and a row carrying no
 *  order cannot cross-filter at all -- the caller decides what to do instead,
 *  which for a barrier is to open it. */
export function toggle(
  selection: Selection | null,
  pane: PaneId,
  row: { orderId: unknown; rowId: string; label?: string | null } | null
): Selection | null {
  if (!row || typeof row.orderId !== 'number') return null;
  // Clicking the same row again clears it. Compared on the row rather than on
  // the order, because two rows of one pane can share an order -- a job with
  // several barriers -- and clicking the second of them should move the
  // highlight rather than clear the filter.
  if (selection && selection.from === pane && selection.rowId === row.rowId) return null;
  return { orderId: row.orderId, from: pane, rowId: row.rowId, label: row.label ?? String(row.orderId) };
}
